// src/hooks/firestore/utils/comparisons.ts
import { QueryConstraint } from 'firebase/firestore';
import { useEffect, useRef } from 'react';

// Utility function to compare query constraints deeply
export function areQueryConstraintsEqual(a: QueryConstraint[], b: QueryConstraint[]): boolean {
  if (a.length !== b.length) return false;
  
  return a.every((constraint, index) => {
    const bConstraint = b[index];
    // Compare the relevant properties of constraints
    // This is a simplified version that compares the stringified versions
    // of the constraint objects to detect actual changes
    return JSON.stringify(constraint) === JSON.stringify(bConstraint);
  });
}

// Custom hook that only runs an effect when dependencies actually change (deep comparison)
export function useDeepCompareEffect(
  effect: React.EffectCallback,
  deps: [string, QueryConstraint[], number, boolean]
) {
  const ref = useRef<[string, QueryConstraint[], number, boolean]>(deps);
  
  // Only update ref if deps have actually changed by deep comparison
  if (!ref.current || 
    deps[0] !== ref.current[0] || 
    !areQueryConstraintsEqual(deps[1], ref.current[1]) ||
    deps[2] !== ref.current[2] ||
    deps[3] !== ref.current[3]
  ) {
    ref.current = deps;
  }
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, [ref.current]);
} 